import type { FC, ReactNode } from 'react';
import { Users, CheckCircle2, FilePenLine, Calendar } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemberCountryRegionStats } from '@/lib/api/hooks/memberCountry';
import { Loader } from '@/components/ui';
import formatDate from '@/utils/helpers/formatDate';

interface StatsCardProps {
  title: string;
  value: number | string;
  description: string;
  icon: ReactNode;
  iconContainerClassName: string;
  iconClassName: string;
}

type StatKey = 'totalCountries' | 'publishedCountries' | 'draftCountries' | 'latestUpdatedAt';

interface StatConfig {
  key: StatKey;
  title: string;
  icon: LucideIcon;
  iconContainerClassName: string;
  iconClassName: string;
  getDescription: (values: Record<StatKey, number | string>) => string;
}

const StatCard = ({
  title,
  value,
  description,
  icon,
  iconContainerClassName,
  iconClassName,
}: StatsCardProps) => (
  <article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
    <div className="grid grid-cols-[auto_1fr] gap-4">
      <div className={`flex h-12 w-12 items-center justify-center rounded ${iconContainerClassName}`}>
        <span className={iconClassName}>{icon}</span>
      </div>
      <div>
        <p className="text-base mb-1 font-medium text-gray-600">{title}</p>
        <p className={`leading-[1.1] font-semibold text-gray-900 ${title.toLowerCase() === 'last updated' ? 'text-[1.25rem]' : 'text-[2rem]'}`}>{value}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  </article>
);

const calculatePercent = (value: number, total: number): string => {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}% of total`;
};

const STATS_CONFIG: StatConfig[] = [
  {
    key: 'totalCountries',
    title: 'Total Countries',
    icon: Users,
    iconContainerClassName: 'bg-[#E8F6EE]',
    iconClassName: 'text-[#0F8A47]',
    getDescription: () => 'In this region',
  },
  {
    key: 'publishedCountries',
    title: 'Published',
    icon: CheckCircle2,
    iconContainerClassName: 'bg-[#E8F6EE]',
    iconClassName: 'text-[#0F8A47]',
    getDescription: ({ publishedCountries, totalCountries }) =>
      calculatePercent(publishedCountries as number, totalCountries as number),
  },
  {
    key: 'draftCountries',
    title: 'Drafts',
    icon: FilePenLine,
    iconContainerClassName: 'bg-[#FDF3E6]',
    iconClassName: 'text-[#B86A00]',
    getDescription: ({ draftCountries, totalCountries }) =>
      calculatePercent(draftCountries as number, totalCountries as number),
  },
  {
    key: 'latestUpdatedAt',
    title: 'Last Updated',
    icon: Calendar,
    iconContainerClassName: 'bg-[#9333ea10]',
    iconClassName: 'text-[#9333ea]',
    getDescription: () => '',
  },
];

interface MemberCountryRegionStatsProps {
  regionId?: string;
}

const MemberCountryRegionStats: FC<MemberCountryRegionStatsProps> = ({ regionId }) => {
  const { data: stats, isFetching, refetch, isError } = useMemberCountryRegionStats(regionId);

  const values: Record<StatKey, number | string> = {
    totalCountries: stats?.totalCountries ?? 0,
    publishedCountries: stats?.publishedCountries ?? 0,
    draftCountries: stats?.draftCountries ?? 0,
    latestUpdatedAt: stats?.latestUpdatedAt ? formatDate(stats.latestUpdatedAt, 'DD MMM YYYY') : '—',
  };

  return (
    <section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 2xl:w-3/4">
      {isError ? (
        <div className="col-span-full p-4 flex flex-col items-center justify-center gap-4">
          <p>Error loading statistics.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">
            Retry
          </button>
        </div>
      ) : isFetching ? (
        STATS_CONFIG.map((stat) => (
          <div key={stat.key} className="p-4 flex items-center justify-center min-h-28 w-full">
            <Loader size="md" variant="wine" />
          </div>
        ))
      ) : (
        STATS_CONFIG.map((stat) => {
          const Icon = stat.icon;
          return (
            <StatCard
              key={stat.key}
              title={stat.title}
              value={values[stat.key]}
              description={stat.getDescription(values)}
              icon={<Icon className="h-4 w-4 md:h-6 md:w-6"  />}
              iconContainerClassName={stat.iconContainerClassName}
              iconClassName={stat.iconClassName}
            />
          );
        })
      )}
    </section>
  );
};

export default MemberCountryRegionStats;
